import { Component, OnInit, OnDestroy } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { Subject, combineLatest, of } from 'rxjs'
import { takeUntil, map, catchError, startWith } from 'rxjs/operators'
import { selectAllRentals, Rental } from '@tokeet-frontend/tv3-platform'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { ComplianceService } from '@tv3/containers/compliance/compliance.service'

const STORAGE_KEY = 'evisitor_discovery_banner_dismissed'

@Component({
  selector: 'app-evisitor-discovery-banner',
  templateUrl: './evisitor-discovery-banner.component.html',
  styleUrls: ['./evisitor-discovery-banner.component.scss'],
})
export class EvisitorDiscoveryBannerComponent implements OnInit, OnDestroy {
  showBanner = false
  private destroy$ = new Subject<void>()

  constructor(
    private store: Store,
    private amplitudeService: AmplitudeService,
    private complianceService: ComplianceService
  ) {}

  ngOnInit(): void {
    if (this.isDismissed()) {
      return
    }

    const hasCroatianRentals$ = this.store.pipe(
      select(selectAllRentals),
      map((rentals: Rental[]) => this.hasCroatianRentals(rentals))
    )

    const hasEvisitorConnections$ = this.complianceService.getEvisitorConnections().pipe(
      map((connections) => connections?.length > 0),
      catchError(() => of(false)),
      startWith(null)
    )

    combineLatest([hasCroatianRentals$, hasEvisitorConnections$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([hasCroatian, hasConnections]) => {
        // Wait for connections to load (hasConnections !== null)
        if (hasConnections === null) {
          return
        }

        const shouldShow = hasCroatian && !hasConnections
        if (shouldShow && !this.showBanner) {
          this.showBanner = true
          this.amplitudeService.logEvent('evisitor_discovery_banner_shown')
        } else if (!shouldShow && this.showBanner) {
          this.showBanner = false
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private hasCroatianRentals(rentals: Rental[]): boolean {
    return rentals?.some((rental) => rental.address?.country?.toLowerCase() === 'croatia')
  }

  private isDismissed(): boolean {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  }

  onDismiss(): void {
    this.showBanner = false
    localStorage.setItem(STORAGE_KEY, 'true')
    this.amplitudeService.logEvent('evisitor_discovery_banner_dismissed')
  }

  onConnect(): void {
    this.amplitudeService.logEvent('evisitor_discovery_banner_connect_clicked')
  }
}
